<template>
  <component :is="embedded ? 'div' : 'v-container'" :fluid="!embedded || undefined" :class="embedded ? 'intelligence-profiles' : 'intelligence-profiles pa-4'">
    <v-row v-if="!embedded">
      <v-col cols="12">
        <h1 class="text-h5 font-weight-bold mb-1 theme-heading">
          Profile Directory
        </h1>
        <p class="text-subtitle-2 grey--text text--darken-1 mb-4">
          Browse and explore talent profiles across your organization
        </p>
      </v-col>
    </v-row>

    <v-card outlined class="pa-4 mb-4">
      <v-row dense>
        <v-col cols="12" md="6">
          <v-text-field
            v-model="searchText"
            label="Search by name, title, or skill..."
            outlined
            dense
            hide-details
            color="#007AFF"
            @input="debouncedSearch"
          >
            <template v-slot:prepend-inner>
              <v-icon color="#007AFF">mdi-magnify</v-icon>
            </template>
          </v-text-field>
        </v-col>
        <v-col cols="12" md="3">
          <v-select
            v-model="selectedCluster"
            :items="clusterOptions"
            label="Cluster"
            outlined
            dense
            clearable
            hide-details
            color="#007AFF"
            @change="applyFilters"
          />
        </v-col>
        <v-col cols="12" md="3">
          <v-select
            v-model="selectedSeniority"
            :items="seniorityOptions"
            label="Seniority"
            outlined
            dense
            clearable
            hide-details
            color="#007AFF"
            @change="applyFilters"
          />
        </v-col>
      </v-row>
    </v-card>

    <v-progress-linear v-if="isLoadingDirectory" indeterminate color="#007AFF" class="mb-4" />

    <v-row v-if="directoryProfiles.length > 0">
      <v-col cols="12" sm="6" md="4" lg="3" v-for="p in directoryProfiles" :key="p.profileId">
        <v-card outlined class="profile-card pa-4" @click="viewProfile(p.profileId)">
          <div class="text-subtitle-2 font-weight-bold theme-heading">{{ p.name }}</div>
          <div class="text-caption grey--text text--darken-1 mb-2">
            {{ p.currentTitle }}
          </div>
          <div class="text-caption grey--text mb-2">{{ p.currentCompany }}</div>
          <div class="d-flex align-center flex-wrap">
            <v-chip x-small outlined color="#007AFF" class="mr-1 mb-1" v-if="p.seniorityLevel">
              {{ p.seniorityLevel }}
            </v-chip>
            <v-chip x-small outlined color="#007AFF" class="mr-1 mb-1">
              <v-icon x-small left>mdi-code-tags</v-icon>
              {{ p.skillCount || 0 }} skills
            </v-chip>
            <v-chip x-small outlined color="#F59E0B" class="mr-1 mb-1">
              <v-icon x-small left>mdi-trophy</v-icon>
              {{ p.achievementCount || 0 }} achievements
            </v-chip>
            <v-chip x-small outlined color="#8B5CF6" class="mr-1 mb-1" v-if="p.credentialsCount > 0">
              <v-icon x-small left>mdi-certificate</v-icon>
              {{ p.credentialsCount }}
            </v-chip>
          </div>
        </v-card>
      </v-col>
    </v-row>

    <div v-else-if="!isLoadingDirectory" class="text-center py-8 grey--text">
      No profiles found matching your criteria
    </div>

    <div class="text-center mt-4" v-if="directory.totalPages > 1">
      <v-pagination
        v-model="currentPage"
        :length="directory.totalPages"
        color="#007AFF"
        @input="changePage"
      />
    </div>
  </component>
</template>

<script>
import { mapState, mapActions } from "vuex";

export default {
  name: "IntelligenceProfiles",
  props: {
    embedded: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      searchText: "",
      selectedCluster: null,
      selectedSeniority: null,
      currentPage: 1,
      searchTimeout: null,
      clusterOptions: [
        "Engineering", "Data Science", "Product", "Design", "Marketing",
        "Operations", "Finance", "HR", "Sales",
      ],
      seniorityOptions: [
        "Junior", "Mid-Level", "Senior", "Staff", "Principal",
        "Director", "VP", "C-Suite",
      ],
    };
  },
  computed: {
    ...mapState("intelligenceProfile", [
      "directory",
      "isLoadingDirectory",
    ]),
    directoryProfiles() {
      return (this.directory && this.directory.profiles) || [];
    },
  },
  mounted() {
    this.loadDirectory();
  },
  methods: {
    ...mapActions("intelligenceProfile", ["loadDirectory"]),
    debouncedSearch() {
      clearTimeout(this.searchTimeout);
      this.searchTimeout = setTimeout(() => {
        this.applyFilters();
      }, 300);
    },
    applyFilters() {
      this.currentPage = 1;
      this.loadDirectory({
        search: this.searchText,
        cluster: this.selectedCluster || "",
        seniority: this.selectedSeniority || "",
        page: 1,
      });
    },
    changePage(page) {
      this.loadDirectory({
        search: this.searchText,
        cluster: this.selectedCluster || "",
        seniority: this.selectedSeniority || "",
        page,
      });
    },
    viewProfile(profileId) {
      this.$router.push({ name: "IntelligenceProfile", params: { profileId }, query: { from: 'directory' } });
    },
  },
};
</script>

<style scoped>
.intelligence-profiles {
  max-width: 1400px;
}
.profile-card {
  cursor: pointer;
  transition: box-shadow 0.2s ease;
}
.profile-card:hover {
  box-shadow: 0 2px 8px rgba(30, 58, 95, 0.15);
}
</style>
